import type { StoreLocatorLocation } from '../../types';
import type { GeocodingSearch } from './use-geocoding-search';

interface SearchFinalItem {
  id?: string;
  placeId?: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  name: string;
  address: string;
  image?: string; // URL for existing location photo
  isExisting?: boolean; // Flag to identify existing locations
}

export const processSearchResults = (
  searchResults: GeocodingSearch[],
  locations: StoreLocatorLocation[],
  query: string
): SearchFinalItem[] => {
  const searchFinal: SearchFinalItem[] = [];
  const usedResults = new Set<string>();

  // Priority 1: Exact placeId matches with images
  searchResults.forEach((result) => {
    const matchingLocation = locations.find(
      (location) => location.place?.placeId === result.id
    );

    if (matchingLocation) {
      searchFinal.push({
        ...result,
        id: matchingLocation.id,
        placeId: result.id,
        image: matchingLocation.photo?.url,
        isExisting: true
      });
      usedResults.add(result.id);
    }
  });

  // Priority 2: Name and address-based matches from existing locations
  const queryLower = query.toLowerCase();
  locations.forEach((location) => {
    const nameMatch = location.name.toLowerCase().includes(queryLower);
    const locationAddressMatch = location.place?.address
      ?.toLowerCase()
      .includes(queryLower);
    const addressMatch = location?.address?.toLowerCase().includes(queryLower);

    if (
      (nameMatch || addressMatch || locationAddressMatch) &&
      !searchResults.some((r) => r.id === location.place?.placeId)
    ) {
      // Create pseudo search result for name or address match
      if (location.place) {
        searchFinal.push({
          id: location.id,
          placeId: location?.place?.placeId,
          coordinates: {
            latitude: location.place.coordinates.lat,
            longitude: location.place.coordinates.lng
          },
          name: location.name,
          address: location.address,
          image: location.photo?.url,
          isExisting: true
        });
      }
    }
  });

  // Priority 3: Remaining geocoding results (no images)
  searchResults.forEach((result) => {
    if (!usedResults.has(result.id)) {
      searchFinal.push({
        ...result,
        placeId: result.id,
        id: undefined,
        isExisting: false
      });
    }
  });

  return searchFinal;
};
